/**
 * Point d'entrée du back-office.
 *
 * Les enveloppes, et rien d'autre : l'internationalisation, le client HTTP, le
 * magasin de session, le routeur du navigateur, et l'application. Le client et
 * le magasin sont **construits ici**, jamais exportés comme instances de
 * module : c'est ce qui rend l'ensemble montable dans un test sans état
 * résiduel, et ce qui permet à un test d'observer les appels réellement émis.
 *
 * La racine d'appel est vide : le back-office est servi par la même origine que
 * l'API, derrière le même filtre d'adresse. Une racine absolue ferait sortir
 * l'appel de cette origine, et le filtre ne le verrait plus.
 */

import { StrictMode } from 'react';

import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import { AnalyticsPageViews, followSession } from './analytics/analytics-bridge.js';
import { deferredAnalytics, startAnalytics } from './analytics/analytics.js';
import { App } from './app/App.js';
import { createApiClient } from './http/api-client.js';
import { attachBearerToken } from './http/back-office-session.js';
import { ApiClientProvider } from './http/api-context.js';
import { createRenewingApiClient } from './http/session-renewal.js';
import { createI18n } from './i18n/index.js';
import { SessionProvider } from './state/session-context.js';
import { createSessionStore } from './state/session-store.js';
import './styles/app.css';

const racine = document.getElementById('root');
if (racine === null) {
  throw new Error(
    "ÉCHEC : l'élément #root est absent de index.html. Sans lui, l'application " +
      "n'a nulle part où se monter, et un écran blanc ne dit pas pourquoi.",
  );
}

const i18n = createI18n();
// Aucun stockage n'est passé : le magasin prend celui du navigateur —
// `sessionStorage`, qui survit à un rechargement et meurt avec l'onglet. C'est
// **le** défaut, et non une option à activer ici : un point d'entrée qui aurait
// à la demander est un point d'entrée qui peut l'oublier, et l'oubli se
// constaterait par une reconnexion complète à chaque `F5`.
const session = createSessionStore({
  onLocaleChange: (locale) => void i18n.changeLanguage(locale),
});
// Le jeton est posé au **point d'extension** du client, et lu à chaque requête
// dans le magasin. Le capturer une fois pour toutes le figerait à sa valeur de
// démarrage — c'est-à-dire à `null`, avant toute connexion.
const transport = createApiClient({
  baseUrl: '',
  prepareRequest: (headers) => attachBearerToken(headers, session.getState().operator?.token),
});
// Le renouvellement enveloppe le transport, il ne le remplace pas (dette `D-30`).
// C'est ce qui fait qu'aucun écran n'a à savoir que la session se renouvelle : ils
// reçoivent tous le même `ApiClient`, et le renouvellement a lieu avant l'échéance
// plutôt qu'après le refus.
const client = createRenewingApiClient({ client: transport, store: session });
// La mesure d'audience (PostHog), éteinte tant que le conteneur ne sert pas de clé.
// Elle ne retarde pas le montage : ses appels attendent son démarrage. Voir
// `analytics/analytics.ts` — ce qui part, et surtout ce qui ne part pas.
const analytics = deferredAnalytics(startAnalytics());
followSession(session, analytics);

createRoot(racine).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <ApiClientProvider client={client}>
        <SessionProvider store={session}>
          <BrowserRouter>
            <AnalyticsPageViews analytics={analytics} />
            <App />
          </BrowserRouter>
        </SessionProvider>
      </ApiClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
